// Універсальна сторінка битви: WAITING (запросити) → ACTIVE (грати) → FINISHED (результат).
// Стан опитується через polling кожні 3с — щоб бачити чи приєднався/завершив суперник.

import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, ApiError, type MatchState } from '../lib/api';
import { Loader } from '../components/Loader';
import { ErrorView } from '../components/ErrorView';
import { PageHeader } from '../components/PageHeader';
import { Progress } from '../components/Progress';
import { Timer } from '../components/Timer';
import { haptic } from '../lib/telegram';
import { IconTile, TabGlyph } from '../components/AppIcons';
import { formatDuration } from '../lib/format';

const BOT_USERNAME = import.meta.env.VITE_BOT_USERNAME ?? 'pdr_testy_bot';
const POLL_INTERVAL_MS = 3000;

export function Battle() {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();

  const [state, setState] = useState<MatchState | null>(null);
  const [error, setError] = useState<Error | null>(null);

  // join — ідемпотентний на бекенді; setState захищений `cancelled` від race в StrictMode.
  useEffect(() => {
    if (!code) return;
    let cancelled = false;

    (async () => {
      try {
        await api.battles.join(code).catch((e) => {
          // 409 = вже учасник / повний / не WAITING; 404 = немає такої — летить далі як stateError
          if (e instanceof ApiError && e.status === 409) return;
          throw e;
        });
        const s = await api.battles.state(code);
        if (!cancelled) setState(s);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e : new Error(String(e)));
      }
    })();

    return () => { cancelled = true; };
  }, [code]);

  // Polling: оновлюємо стан кожні 3с поки матч не FINISHED і ми ще на сторінці
  useEffect(() => {
    if (!code || !state || state.status === 'FINISHED' || state.status === 'CANCELLED') return;
    const t = setInterval(async () => {
      try {
        const s = await api.battles.state(code);
        setState((prev) => {
          // Сповіщаємо коли суперник приєднався
          if (prev?.status === 'WAITING' && s.status === 'ACTIVE') haptic.success();
          return s;
        });
      } catch {
        // ignore — наступний tick спробує ще раз
      }
    }, POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, [code, state]);

  if (!code) return <ErrorView error={new Error('Невалідний код')} />;
  if (error) return <ErrorView error={error} />;
  if (!state) return <Loader label="Завантаження битви…" />;

  if (state.status === 'WAITING') return <Waiting code={code} state={state} />;
  if (state.status === 'ACTIVE') return <Playing code={code} state={state} setState={setState} navigate={navigate} />;
  if (state.status === 'FINISHED') return <Finished state={state} navigate={navigate} />;
  return <Cancelled navigate={navigate} />;
}

// ===== CANCELLED — битва скасована (TTL або власноруч) =====
function Cancelled({ navigate }: { navigate: ReturnType<typeof useNavigate> }) {
  return (
    <div className="flex flex-col items-center gap-4 p-6 text-center">
      <div className="text-5xl">⚠️</div>
      <p className="text-tg-text">Битву скасовано</p>
      <p className="text-xs text-tg-hint">Можливо, минув час очікування або суперник не приєднався.</p>
      <button onClick={() => navigate('/battles', { replace: true })} className="btn-primary mt-2">
        До списку битв
      </button>
      <button onClick={() => navigate('/battles/new', { replace: true })} className="btn-secondary">
        Створити нову
      </button>
    </div>
  );
}

// ===== WAITING — творець бачить кнопку поділитись, гість не доходить сюди =====
function Waiting({ code, state }: { code: string; state: MatchState }) {
  const navigate = useNavigate();
  const inviteUrl = `https://t.me/${BOT_USERNAME}?start=battle_${code}`;
  const [copied, setCopied] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  function copyLink() {
    navigator.clipboard.writeText(inviteUrl).then(() => {
      setCopied(true);
      haptic.light();
      setTimeout(() => setCopied(false), 1500);
    });
  }

  async function cancel() {
    if (cancelling) return;
    if (!confirm('Скасувати битву? Її більше не можна буде запустити.')) return;
    setCancelling(true);
    haptic.light();
    try {
      await api.battles.cancel(code);
      navigate('/battles', { replace: true });
    } catch {
      // На крайній випадок — просто йдемо назад, бо за TTL вона все одно скасується
      navigate('/battles', { replace: true });
    }
  }

  function shareViaTelegram() {
    const text = `⚔️ Виклик на битву ПДР! Натисни щоб прийняти:`;
    const url = `https://t.me/share/url?url=${encodeURIComponent(inviteUrl)}&text=${encodeURIComponent(text)}`;
    // Telegram WebApp має openTelegramLink — він не закриває Mini App, відкриває оверлей
    const tg = (window as { Telegram?: { WebApp?: { openTelegramLink?: (u: string) => void } } }).Telegram?.WebApp;
    if (tg?.openTelegramLink) tg.openTelegramLink(url);
    else window.open(url, '_blank');
    haptic.light();
  }

  return (
    <>
      <PageHeader
        title="Очікування"
        subtitle="Поділись посиланням з другом"
        backTo="/battles"
      />
      <div className="space-y-4 p-4">
        <section className="card flex flex-col items-center text-center">
          <IconTile kind="battle" className="mb-2 h-12 w-12" glyphClass="h-7 w-7" />
          <div className="font-medium">Битву створено!</div>
          <div className="mt-1 text-xs text-tg-hint">
            {state.questionCount} питань · {state.category?.nameUk ?? 'Усі категорії'}
          </div>
        </section>

        <section className="card space-y-2">
          <label className="block text-xs font-medium text-tg-hint">Код битви</label>
          <div className="text-center font-mono text-2xl font-bold tracking-widest">{code}</div>
        </section>

        <section className="card space-y-2">
          <label className="block text-xs font-medium text-tg-hint">Посилання-запрошення</label>
          <div className="break-all rounded-lg bg-tg-bg p-2 text-xs">{inviteUrl}</div>
          <button onClick={shareViaTelegram} className="btn-primary w-full">
            Поділитись у Telegram
          </button>
          <button onClick={copyLink} className="btn-secondary w-full">
            {copied ? '✓ Скопійовано' : 'Скопіювати посилання'}
          </button>
        </section>

        <p className="card text-center text-sm text-tg-hint">
          Чекаю на суперника… <span className="inline-block animate-pulse">●</span>
        </p>

        <button
          onClick={cancel}
          disabled={cancelling}
          className="btn-secondary w-full text-tg-destructive"
        >
          ✕ Скасувати битву
        </button>
      </div>
    </>
  );
}

// ===== ACTIVE — режим тесту з прогресом обох гравців у header =====
type AnswerState = { selectedId: number | null; isCorrect: boolean; correctAnswerId: number | null };

function Playing({
  code,
  state,
  setState,
  navigate,
}: {
  code: string;
  state: MatchState;
  setState: (s: MatchState) => void;
  navigate: ReturnType<typeof useNavigate>;
}) {
  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<number, AnswerState>>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const startRef = useRef(Date.now());

  useEffect(() => { startRef.current = Date.now(); }, [index]);

  const total = state.questionCount;
  const current = state.questions[index];
  const currentAns = current ? answers[current.id] : undefined;
  const meDone = state.me?.finishedAt !== null && state.me?.finishedAt !== undefined;

  if (!current) return <Loader />;

  async function pick(answerId: number) {
    if (submitting || !current || currentAns) return;
    setSubmitting(true);
    setError(null);
    const timeMs = Date.now() - startRef.current;
    try {
      const res = await api.battles.answer(code, { questionId: current.id, answerId, timeMs });
      setAnswers((p) => ({ ...p, [current.id]: { selectedId: answerId, ...res } }));
      if (res.isCorrect) haptic.success(); else haptic.error();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Помилка');
      haptic.error();
    } finally {
      setSubmitting(false);
    }
  }

  async function next() {
    haptic.light();
    if (index < total - 1) {
      setIndex(index + 1);
    } else {
      // Завершуємо свою сторону — суперник може ще грати
      try {
        await api.battles.finish(code);
        const s = await api.battles.state(code);
        setState(s);
        if (s.status === 'FINISHED') navigate(`/battles/${code}`, { replace: true });
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Помилка завершення');
      }
    }
  }

  // Якщо я вже завершив — чекаю суперника
  if (meDone) {
    return (
      <>
        <PageHeader title="⏱ Чекаю суперника" />
        <div className="space-y-4 p-4">
          <OpponentBar state={state} mode="active" />
          <p className="card text-center text-sm text-tg-hint">
            Ти завершив. Чекаю поки суперник доб'є.
          </p>
        </div>
      </>
    );
  }

  return (
    <>
      <header className="sticky top-0 z-10 border-b border-tg-secondary-bg bg-tg-bg/95 backdrop-blur">
        <div className="space-y-2 px-4 pt-[env(safe-area-inset-top)] pb-3">
          <div className="flex items-center justify-between pt-3">
            <button
              onClick={() => {
                if (confirm('Здатись? Битва буде втрачена.')) {
                  api.battles.finish(code).finally(() => navigate('/battles', { replace: true }));
                }
              }}
              className="text-sm text-tg-destructive"
            >
              Здатись
            </button>
            <Timer
              totalSeconds={state.timeLimitSec}
              onExpire={() => api.battles.finish(code).then(() => navigate(`/battles/${code}`, { replace: true }))}
            />
          </div>
          <Progress current={index + 1} total={total} />
          <OpponentBar state={state} mode="active" />
        </div>
      </header>

      <div className="space-y-4 p-4">
        {error && <div className="card border border-tg-destructive text-sm text-tg-destructive">{error}</div>}

        <article className="card">
          <p className="mb-3 text-base">{current.textUk}</p>
          {current.imageUrl && (
            <img src={current.imageUrl} alt="" className="w-full rounded-lg bg-tg-bg object-contain" loading="lazy" />
          )}
        </article>

        <div className="space-y-2">
          {current.answers.map((a, i) => {
            const isPicked = currentAns?.selectedId === a.id;
            const isRight = currentAns?.correctAnswerId === a.id;
            let cls = 'card flex w-full items-center gap-3 text-left transition';
            if (currentAns) {
              if (isRight) cls += ' border border-tg-success bg-tg-success/10';
              else if (isPicked) cls += ' border border-tg-destructive bg-tg-destructive/10';
              else cls += ' opacity-60';
            } else cls += ' hover:bg-tg-bg/40';
            return (
              <button
                key={a.id}
                disabled={submitting || !!currentAns}
                onClick={() => pick(a.id)}
                className={cls}
              >
                <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-tg-bg text-xs font-semibold">
                  {String.fromCharCode(65 + i)}
                </span>
                <span className="flex-1">{a.textUk}</span>
                {currentAns && isRight && <span>✓</span>}
                {currentAns && isPicked && !isRight && <span>✗</span>}
              </button>
            );
          })}
        </div>

        {currentAns && (
          <button onClick={next} disabled={submitting} className="btn-primary w-full">
            {index < total - 1 ? 'Далі' : 'Завершити свою сторону'}
          </button>
        )}
      </div>
    </>
  );
}

// ===== Бар з прогресом обох гравців (видно під час гри і у waiting) =====
function OpponentBar({ state, mode }: { state: MatchState; mode: 'active' | 'finished' }) {
  const me = state.me;
  const opp = state.opponent;
  const total = state.questionCount;

  return (
    <div className="grid grid-cols-2 gap-2 text-xs">
      <PlayerStat name="Ти" answered={me?.answeredCount ?? 0} score={me?.score ?? 0} total={total} done={!!me?.finishedAt} mode={mode} />
      <PlayerStat
        name={opp?.user.firstName ?? opp?.user.username ?? 'Суперник'}
        answered={opp?.answeredCount ?? 0}
        score={opp?.score ?? 0}
        total={total}
        done={!!opp?.finishedAt}
        mode={mode}
        photoUrl={opp?.user.photoUrl ?? null}
      />
    </div>
  );
}

function PlayerStat({
  name,
  answered,
  score,
  total,
  done,
  mode,
  photoUrl,
}: {
  name: string;
  answered: number;
  score: number;
  total: number;
  done: boolean;
  mode: 'active' | 'finished';
  photoUrl?: string | null;
}) {
  const pct = total ? (answered / total) * 100 : 0;
  return (
    <div className="rounded-lg bg-tg-secondary-bg p-2">
      <div className="flex items-center gap-2">
        {photoUrl ? (
          <img src={photoUrl} alt="" className="h-5 w-5 rounded-full object-cover" />
        ) : (
          <div className="grid h-5 w-5 place-items-center rounded-full bg-tg-bg text-tg-hint">
            <TabGlyph kind="profile" className="h-3 w-3" />
          </div>
        )}
        <span className="truncate font-medium">{name}</span>
        {done && <span className="text-tg-success">✓</span>}
      </div>
      <div className="mt-1 h-1 overflow-hidden rounded-full bg-tg-bg">
        <div className="h-full bg-tg-button transition-all" style={{ width: `${pct}%` }} />
      </div>
      <div className="mt-1 text-tg-hint">
        {mode === 'finished' ? `${score}/${total}` : `${answered}/${total} (${score} ✓)`}
      </div>
    </div>
  );
}

// ===== FINISHED — фінальний рахунок =====
function Finished({ state, navigate }: { state: MatchState; navigate: ReturnType<typeof useNavigate> }) {
  const me = state.me!;
  const opp = state.opponent;
  const myUserId = me ? '?' : ''; void myUserId; // utility unused holder
  // Визначаємо результат відносно мене за winnerId
  const isWinner = state.winnerId !== null && state.winnerId !== undefined && opp !== null && state.winnerId !== opp.user.id;
  const isDraw = state.isDraw;

  const headline = isDraw ? '🤝 Нічия' : isWinner ? '🏆 Перемога!' : '💀 Поразка';
  const headlineColor = isDraw ? 'text-tg-text' : isWinner ? 'text-tg-success' : 'text-tg-destructive';

  const matchDuration = state.startedAt && state.finishedAt
    ? new Date(state.finishedAt).getTime() - new Date(state.startedAt).getTime()
    : null;

  return (
    <>
      <PageHeader title="Результат" backTo="/battles" />
      <div className="space-y-4 p-4">
        <section className="card flex flex-col items-center gap-2 py-8">
          <div className={`text-2xl font-bold ${headlineColor}`}>{headline}</div>
          <div className="mt-2 grid grid-cols-2 gap-4 text-center">
            <div>
              <div className="text-xs text-tg-hint">Ти</div>
              <div className="text-3xl font-bold">{me.score}</div>
            </div>
            <div>
              <div className="text-xs text-tg-hint">{opp?.user.firstName ?? 'Суперник'}</div>
              <div className="text-3xl font-bold">{opp?.score ?? '—'}</div>
            </div>
          </div>
          {matchDuration && (
            <div className="mt-2 text-xs text-tg-hint">Тривалість: {formatDuration(matchDuration)}</div>
          )}
        </section>

        <OpponentBar state={state} mode="finished" />

        <div className="space-y-2 pt-2">
          <button onClick={() => navigate('/battles/new')} className="btn-primary w-full">
            Реванш / Нова битва
          </button>
          <button onClick={() => navigate('/battles')} className="btn-secondary w-full">
            До списку
          </button>
        </div>
      </div>
    </>
  );
}
